import { DEFAULT_HEATMAP_GRADIENT, metersToPixels } from './heatmapUtils';

/**
 * Build a 256-step RGBA palette from gradient color strings.
 * @param {string[]} gradient
 */
function buildPalette(gradient) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  const grd = ctx.createLinearGradient(0, 0, 256, 0);
  const step = 1 / (gradient.length - 1);
  gradient.forEach((color, i) => {
    grd.addColorStop(i * step, color);
  });
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, 256, 1);
  return ctx.getImageData(0, 0, 256, 1).data;
}

/**
 * Canvas-based heatmap overlay for Google Maps (replaces deprecated HeatmapLayer).
 * API: setMap(map|null), setData(points), setRadius(meters)
 */
export function createCanvasHeatmapOverlayClass(googleMaps) {
  const paletteCache = new Map();

  function getPalette(gradient) {
    const key = gradient.join('|');
    if (!paletteCache.has(key)) {
      paletteCache.set(key, buildPalette(gradient));
    }
    return paletteCache.get(key);
  }

  return class CanvasHeatmapOverlay extends googleMaps.OverlayView {
    constructor(options = {}) {
      super();
      this.points = options.points || [];
      this.radiusMeters = options.radius ?? 40;
      this.opacity = options.opacity ?? 0.85;
      this.gradient = options.gradient || DEFAULT_HEATMAP_GRADIENT;
      this._listeners = [];
      this._container = null;
      this._canvas = null;
      this._shadowCanvas = null;
    }

    onAdd() {
      this._container = document.createElement('div');
      this._container.style.position = 'absolute';
      this._container.style.pointerEvents = 'none';

      this._canvas = document.createElement('canvas');
      this._shadowCanvas = document.createElement('canvas');
      this._container.appendChild(this._canvas);

      const panes = this.getPanes();
      panes.overlayLayer.appendChild(this._container);

      const redraw = () => this.draw();
      this._listeners = [
        this.getMap().addListener('bounds_changed', redraw),
        this.getMap().addListener('zoom_changed', redraw),
        this.getMap().addListener('idle', redraw),
      ];
      this.draw();
    }

    onRemove() {
      this._listeners.forEach(l => l.remove());
      this._listeners = [];
      if (this._container?.parentNode) {
        this._container.parentNode.removeChild(this._container);
      }
      this._container = null;
      this._canvas = null;
      this._shadowCanvas = null;
    }

    draw() {
      if (!this._canvas || !this.getMap()) return;

      const projection = this.getProjection();
      if (!projection) return;

      const map = this.getMap();
      const bounds = map.getBounds();
      if (!bounds) return;

      const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
      const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
      if (!ne || !sw) return;

      const width = Math.max(1, Math.ceil(ne.x - sw.x));
      const height = Math.max(1, Math.ceil(sw.y - ne.y));

      this._container.style.left = `${sw.x}px`;
      this._container.style.top = `${ne.y}px`;
      this._container.style.width = `${width}px`;
      this._container.style.height = `${height}px`;

      this._canvas.width = width;
      this._canvas.height = height;
      this._shadowCanvas.width = width;
      this._shadowCanvas.height = height;

      const shadowCtx = this._shadowCanvas.getContext('2d');
      const ctx = this._canvas.getContext('2d');
      shadowCtx.clearRect(0, 0, width, height);
      ctx.clearRect(0, 0, width, height);

      if (!this.points.length) return;

      const zoom = map.getZoom();
      const centerLat = map.getCenter()?.lat() ?? this.points[0].lat;
      const radiusPx = Math.max(8, metersToPixels(this.radiusMeters, centerLat, zoom));

      shadowCtx.globalCompositeOperation = 'lighter';

      this.points.forEach(point => {
        const pixel = projection.fromLatLngToDivPixel(new googleMaps.LatLng(point.lat, point.lng));
        if (!pixel) return;

        const x = pixel.x - sw.x;
        const y = pixel.y - ne.y;
        const weight = point.weight ?? 1;
        const intensity = Math.min(1, weight);

        const gradient = shadowCtx.createRadialGradient(x, y, 0, x, y, radiusPx);
        gradient.addColorStop(0, `rgba(0,0,0,${intensity})`);
        gradient.addColorStop(0.4, `rgba(0,0,0,${intensity * 0.6})`);
        gradient.addColorStop(1, 'rgba(0,0,0,0)');

        shadowCtx.fillStyle = gradient;
        shadowCtx.beginPath();
        shadowCtx.arc(x, y, radiusPx, 0, 2 * Math.PI);
        shadowCtx.fill();
      });

      const palette = getPalette(this.gradient);
      const shadowData = shadowCtx.getImageData(0, 0, width, height);
      const colored = ctx.createImageData(width, height);
      const src = shadowData.data;
      const dest = colored.data;

      for (let i = 0; i < src.length; i += 4) {
        const alpha = src[i + 3];
        if (alpha === 0) continue;
        const idx = alpha * 4;
        dest[i] = palette[idx];
        dest[i + 1] = palette[idx + 1];
        dest[i + 2] = palette[idx + 2];
        dest[i + 3] = Math.round(alpha * this.opacity);
      }

      ctx.putImageData(colored, 0, 0);
    }

    /** @param {Array<{ lat: number, lng: number, weight?: number }>} points */
    setData(points) {
      this.points = points;
      this.draw();
    }

    setRadius(radiusMeters) {
      this.radiusMeters = radiusMeters;
      this.draw();
    }

    setOpacity(opacity) {
      this.opacity = opacity;
      this.draw();
    }

    setGradient(gradient) {
      this.gradient = gradient || DEFAULT_HEATMAP_GRADIENT;
      this.draw();
    }
  };
}

let overlayClass = null;

/**
 * Create a heatmap overlay instance attached to a map.
 * @returns {InstanceType<ReturnType<typeof createCanvasHeatmapOverlayClass>> | null}
 */
export function createHeatmapOverlay(map, options = {}) {
  if (!map || !window.google?.maps) return null;

  if (!overlayClass) {
    overlayClass = createCanvasHeatmapOverlayClass(window.google.maps);
  }

  const overlay = new overlayClass({
    points: options.points || [],
    radius: options.radius ?? 40,
    opacity: options.opacity ?? 0.85,
    gradient: options.gradient || DEFAULT_HEATMAP_GRADIENT,
  });
  overlay.setMap(map);
  return overlay;
}
